import { useState } from 'react';
import { ButtonText, FormFeedback } from '@morressier/alembic';
import { Box, Flex } from '@chakra-ui/react';
import { FormFieldTitle } from '@publishing-engine/domain/types/article';
import { useFormContext } from 'react-hook-form';
import { ISubmissionFormData } from 'src/contexts/FormContext';
import { FieldLabel } from 'src/components/Field';
import RichTextPreview from 'src/components/Submission/GenericFieldComponents/RichText/RichTextPreview';
import RichTextSubmission from 'src/components/Submission/GenericFieldComponents/RichText/RichTextSubmission';
import { get } from 'lodash';

export default function Title({
  config,
  isMetadataExtracted,
  editTitle
}: {
  config: FormFieldTitle;
  isMetadataExtracted: boolean;
  editTitle: string;
}) {
  const form = useFormContext<ISubmissionFormData>();
  const {
    params: { validations, labels },
    caseDataFields: { main: field }
  } = config;

  const { getValues } = form;
  const {
    metadata: { title }
  } = getValues();

  const error = get(form.formState.errors, field);

  const [isEditing, setIsEditing] = useState<boolean>(!title);

  const renderReadView = () => (
    <Flex flexDirection="column" gap={4} data-cy="read-view">
      <Box textStyle="headingSection" data-cy="title-preview">
        <RichTextPreview value={title} />
      </Box>

      <ButtonText
        width="fit-content"
        variant="primary"
        onClick={() => setIsEditing(true)}
        data-cy="edit-title-button"
      >
        {editTitle}
      </ButtonText>
      {Boolean(error) && (
        <FormFeedback variant="error">
          {error?.type === 'richTextMinLength' ||
          error?.message?.includes('must NOT have fewer than 1')
            ? `${labels.main} is required`
            : error?.type === 'richTextMaxLength'
            ? `${labels.main} must not exceed the maximum allowed length`
            : `${labels.main} ${error?.message}`}
        </FormFeedback>
      )}
    </Flex>
  );

  const renderEditView = () => (
    <Flex flexDirection="column" gap={4} data-cy="edit-view">
      <FieldLabel
        isRequired={validations.required}
        label={labels.main}
        subText={labels.subtitle}
        helpText={labels.helpText}
        data-cy="field-label"
      />

      <RichTextSubmission
        initialValue={title}
        label={labels.main}
        field={field}
        maxWords={validations?.maxWords}
        data-cy="rich-text-submission"
      />
    </Flex>
  );

  return isMetadataExtracted && !isEditing
    ? renderReadView()
    : renderEditView();
}
